/**
 * Cockpit (WebAdminCC) JWT auth — opt-in path alongside the legacy pipe-Auth Session.
 *
 * Activation: localStorage key `sign_in_jwt` present. Username can't be scoped on the flag —
 * WebAdminCC doesn't know the typed-in username before the SignInService redirect. Tester
 * sets `localStorage.setItem("sign_in_jwt", "1")` in DevTools to enable; removes to disable.
 *
 * Storage policy: access + refresh + ECDH ephemeral private all live in `sessionStorage` (per
 * tab, cleared on browser close). Cross-tab handoff goes through the existing session bridge
 * (extended payload). Refresh is coordinated by Web Locks + BroadcastChannel — only one tab
 * calls `/oauth/refresh` even when many tabs simultaneously hit token expiry; peers update
 * their sessionStorage from the broadcast.
 */

import { getConfig, notifySessionExpired } from "../api/client.ts";

const TOKENS_KEY = "cockpit-tokens";
const EPH_PRIV_KEY = "cockpit-eph-priv";
const REFRESH_LOCK = "cockpit-jwt-refresh";
const BCAST_NAME = "cockpit-jwt";
const REFRESH_LEEWAY_SEC = 30;
/** Wider window used by the wake-up refresh at the bottom of this file: a token with less than
 *  this much life left is renewed while the tab is foregrounded, instead of at the moment the
 *  next request needs it. Access tokens live 5 minutes (JwtIssuer.AccessTokenLifetime), so a
 *  phone whose screen was off for longer than that always needs a refresh on wake. */
const WAKE_REFRESH_LEEWAY_SEC = 120;
/** Abort a refresh POST that hangs — a stalled one would block every call queued behind it. */
const REFRESH_TIMEOUT_MS = 10_000;
/** Backoff before the single retry of a refresh that never reached the server. */
const RETRY_DELAY_MS = 600;
/** Longest server-suggested Retry-After we will wait inline. Anything longer means a real
 *  rate-limit lockout, where retrying is pointless. */
const MAX_RETRY_AFTER_MS = 2_000;
/** How long to wait for the `online` event before attempting a refresh while offline. */
const ONLINE_WAIT_MS = 5_000;

export interface CockpitTokens {
  access: string;
  refresh: string;
  expUnix: number;
  userId: number;
  scope: string;
  /** Stable per-sign-in chain identifier from the server. Used to scope cross-tab broadcast
   * updates so a sibling tab signed in separately can't overwrite this tab's chain — see
   * the message handler in {@link getBcast}. */
  chainId: string;
}

interface BroadcastMessage {
  type: "tokens" | "clear";
  payload?: CockpitTokens;
  /** ChainId the sender is broadcasting *from* (i.e. the chain whose tokens just rotated /
   * cleared). Recipients only adopt the message if it matches their own chainId. */
  chainId?: string;
}

let bcast: BroadcastChannel | null = null;

function getBcast(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!bcast) {
    bcast = new BroadcastChannel(BCAST_NAME);
    bcast.addEventListener("message", (ev: MessageEvent<BroadcastMessage>) => {
      const msg = ev.data;
      if (!msg) return;
      try {
        const current = getTokens();
        // Cross-chain isolation: a tab signed in separately (different chainId) must not have
        // its sessionStorage tokens overwritten by a peer's rotation, or its next refresh
        // would present an already-spent token and trigger server-side reuse-detection that
        // revokes the wrong chain. Adopt only if either (a) the recipient has no tokens yet
        // (post-bridge new tab consuming the original chain) or (b) chainIds match.
        const senderChain = msg.chainId ?? msg.payload?.chainId ?? "";
        const mineChain = current?.chainId ?? "";
        if (current && mineChain && senderChain && mineChain !== senderChain) return;
        if (msg.type === "tokens" && msg.payload) {
          sessionStorage.setItem(TOKENS_KEY, JSON.stringify(msg.payload));
        } else if (msg.type === "clear") {
          sessionStorage.removeItem(TOKENS_KEY);
        }
      } catch { /* sessionStorage unavailable — ignore */ }
    });
  }
  return bcast;
}

/**
 * Eager-subscribe to the cross-tab token channel as soon as this module loads. Without this
 * a tab opened by the session-bridge handoff would sit idle (never call `getAccessToken` /
 * `clearTokens`) and miss broadcasts from its sibling — then later refresh with a stale
 * token and trigger server reuse-detection, revoking the chain for both tabs.
 */
if (typeof window !== "undefined") {
  try { getBcast(); } catch { /* ignore */ }
}

/**
 * Decode a JWT payload (middle segment, base64url JSON). Pure parse — no signature verification;
 * server already validated when issuing or refreshing. Returns the parsed claims object or null
 * on malformed input. Use only for non-security UI hints (SuperAdmin badge, banner copy) —
 * authority always rests with server-side validation.
 */
export function decodeJwtClaims(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    const payloadB64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadB64 + "===".slice((payloadB64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch { return null; }
}

export function getTokenSuperAdmin(): boolean {
  const t = getTokens();
  if (!t) return false;
  const claims = decodeJwtClaims(t.access);
  return claims?.["superadmin"] === true;
}

export function getTokenSuperUserTools(): boolean {
  const t = getTokens();
  if (!t) return false;
  const claims = decodeJwtClaims(t.access);
  return claims?.["superusertools"] === true;
}

export function getTokens(): CockpitTokens | null {
  try {
    const raw = sessionStorage.getItem(TOKENS_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as CockpitTokens;
    if (typeof t.access !== "string" || typeof t.refresh !== "string") return null;
    return t;
  } catch { return null; }
}

export function setTokens(t: CockpitTokens | null): void {
  try {
    if (t) sessionStorage.setItem(TOKENS_KEY, JSON.stringify(t));
    else sessionStorage.removeItem(TOKENS_KEY);
  } catch { /* ignore */ }
}

export function clearTokens(): void {
  const t = getTokens();
  setTokens(null);
  getBcast()?.postMessage({ type: "clear", chainId: t?.chainId } satisfies BroadcastMessage);
}

export function setEphPriv(jwk: JsonWebKey): void {
  try { sessionStorage.setItem(EPH_PRIV_KEY, JSON.stringify(jwk)); } catch { /* ignore */ }
}

/** One-shot read + delete. Returns null if not present or malformed. */
export function consumeEphPriv(): JsonWebKey | null {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(EPH_PRIV_KEY); } catch { return null; }
  if (!raw) return null;
  try { sessionStorage.removeItem(EPH_PRIV_KEY); } catch { /* ignore */ }
  try { return JSON.parse(raw) as JsonWebKey; } catch { return null; }
}

/** True iff localStorage key `sign_in_jwt` is present (any value). Test opt-in flag. */
export function isJwtOptIn(): boolean {
  try {
    return localStorage.getItem("sign_in_jwt") !== null;
  } catch { /* localStorage unavailable */ }
  return false;
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  userId: number;
  scope: string;
  chain_id: string;
}

/**
 * Classified `/oauth/refresh` failure. The distinction is the whole point: the previous code
 * treated every failure as "this credential is dead" and cleared the tokens, so a phone waking
 * up with its radio still reconnecting got signed out even though its refresh token was fine.
 *
 * - `fatal` (400 / 401) — the server rejected the credential itself: invalid token, reuse
 *   detected (chain revoked), account no longer eligible. Nothing to retry, sign the user out.
 * - `retryable` — the server provably never rotated the token (429 is checked before
 *   `RefreshTokenStore.Rotate`), or the request never got a response at all.
 * - anything else (403 origin, 5xx, malformed body) — keep the tokens, fail only this call.
 *   5xx is deliberately NOT retried: an edge 502 can arrive after the backend already rotated,
 *   and presenting the spent token again would trip reuse-detection and revoke the whole chain.
 */
class RefreshFailure extends Error {
  /** HTTP status, or null when no response was received at all (network-level failure). */
  readonly status: number | null;
  /** Server-supplied `Retry-After` in ms, uncapped, when one was readable. */
  readonly retryAfterMs: number | null;
  /** True when our own timeout aborted the request — the server may still have processed it,
   *  so this is never retried. */
  readonly timedOut: boolean;

  constructor(
    message: string,
    opts: { status?: number | null; retryAfterMs?: number | null; timedOut?: boolean } = {},
  ) {
    super(message);
    this.name = "RefreshFailure";
    this.status = opts.status ?? null;
    this.retryAfterMs = opts.retryAfterMs ?? null;
    this.timedOut = opts.timedOut === true;
  }

  get fatal(): boolean {
    return this.status === 400 || this.status === 401;
  }

  get retryable(): boolean {
    if (this.timedOut) return false;
    // No response at all: on a phone this is almost always the request never leaving the
    // device (radio still down). A reset connection could in theory drop a response after the
    // backend rotated, and the retry would then trip reuse-detection — accepted, because the
    // alternative is the guaranteed sign-out this whole classification exists to remove.
    if (this.status === null) return true;
    if (this.status !== 429) return false;
    // A lockout answers with a Retry-After far beyond what we would wait inline. That header is
    // only readable when SignInService's CORS policy exposes it; unreadable → treat as a blip.
    return this.retryAfterMs === null || this.retryAfterMs <= MAX_RETRY_AFTER_MS;
  }

  /** Delay before the retry this failure permits. */
  get retryDelayMs(): number {
    return Math.min(this.retryAfterMs ?? RETRY_DELAY_MS, MAX_RETRY_AFTER_MS);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseRetryAfterMs(res: Response): number | null {
  const raw = res.headers.get("Retry-After");
  if (!raw) return null;
  const secs = Number(raw);
  if (!Number.isFinite(secs) || secs < 0) return null;
  return secs * 1000;
}

/** Resolve once the browser reports connectivity again, or after ONLINE_WAIT_MS. `navigator.onLine`
 *  is only a hint — `false` is reliable, `true` is not — which is exactly how it is used here:
 *  don't burn a refresh attempt while the device knows it has no link. */
function waitForOnline(): Promise<void> {
  if (typeof navigator === "undefined" || navigator.onLine !== false) return Promise.resolve();
  if (typeof window === "undefined") return Promise.resolve();
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("online", finish);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, ONLINE_WAIT_MS);
    window.addEventListener("online", finish);
  });
}

async function postRefresh(refreshToken: string): Promise<CockpitTokens> {
  const { SignInServiceUrl } = getConfig();
  const url = `${SignInServiceUrl.replace(/\/+$/, "")}/oauth/refresh`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
      signal: typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
        ? AbortSignal.timeout(REFRESH_TIMEOUT_MS)
        : undefined,
    });
  } catch (err) {
    const name = err instanceof Error ? err.name : "";
    throw new RefreshFailure(
      `/oauth/refresh unreachable: ${err instanceof Error ? err.message : String(err)}`,
      { timedOut: name === "TimeoutError" || name === "AbortError" },
    );
  }
  if (!res.ok) {
    throw new RefreshFailure(`/oauth/refresh failed: ${res.status}`, {
      status: res.status,
      retryAfterMs: parseRetryAfterMs(res),
    });
  }
  let body: RefreshResponse;
  try {
    body = (await res.json()) as RefreshResponse;
  } catch (err) {
    // 200 with an unusable body — not a credential problem, but a retry won't fix it either.
    throw new RefreshFailure(
      `/oauth/refresh returned malformed JSON: ${err instanceof Error ? err.message : String(err)}`,
      { status: res.status },
    );
  }
  if (typeof body?.access_token !== "string" || typeof body?.refresh_token !== "string") {
    throw new RefreshFailure("/oauth/refresh returned no tokens", { status: res.status });
  }
  return {
    access: body.access_token,
    refresh: body.refresh_token,
    expUnix: Math.floor(Date.now() / 1000) + body.expires_in,
    userId: body.userId,
    scope: body.scope,
    chainId: body.chain_id ?? "",
  };
}

/** One refresh attempt: rotate, store, tell the peer tabs. Returns the new access token. */
async function attemptRefresh(refreshToken: string): Promise<string> {
  const next = await postRefresh(refreshToken);
  setTokens(next);
  getBcast()?.postMessage({ type: "tokens", payload: next, chainId: next.chainId } satisfies BroadcastMessage);
  return next.access;
}

/** Refresh under the retry policy documented on {@link RefreshFailure}. Throws on failure. */
async function refreshWithPolicy(refreshToken: string): Promise<string> {
  await waitForOnline();
  try {
    return await attemptRefresh(refreshToken);
  } catch (err) {
    if (!(err instanceof RefreshFailure) || !err.retryable) throw err;
    await sleep(err.retryDelayMs);
    await waitForOnline();
    return attemptRefresh(refreshToken);
  }
}

function needsRefresh(t: CockpitTokens, leewaySec: number): boolean {
  return Math.floor(Date.now() / 1000) >= t.expUnix - leewaySec;
}

/**
 * Returns a usable access token, refreshing when it is within `leewaySec` of expiry.
 * Cross-tab safe: the actual `/oauth/refresh` POST is serialized through `navigator.locks`,
 * and the refreshed tokens are broadcast to peer tabs over the cockpit-jwt BroadcastChannel —
 * so 50 tabs hitting expiry simultaneously result in exactly one network refresh.
 *
 * Two failure shapes, and the difference matters:
 *   - **`null`** — the credential is dead (400/401: revoked chain, reuse detected, account
 *     gone). Tokens are cleared and the caller's `if (!access)` branch fires the
 *     session-expired callback, i.e. the user is signed out.
 *   - **throws** — transient (offline, timeout, 5xx, rate-limited, config not loaded yet).
 *     Tokens are left intact and only this one call fails, so a phone whose radio hadn't come
 *     back yet keeps its session and succeeds on the next attempt. Callers already cope with a
 *     rejected `servicePost` — a plain `fetch` failure looks the same to them.
 */
export async function getAccessToken(leewaySec: number = REFRESH_LEEWAY_SEC): Promise<string | null> {
  const t0 = getTokens();
  if (!t0) return null;
  if (!needsRefresh(t0, leewaySec)) return t0.access;

  const run = async (): Promise<string | null> => {
    // Re-read after acquiring the lock — a peer tab may have refreshed while we waited and
    // broadcast the new tokens (the BroadcastChannel listener writes them to sessionStorage).
    const t = getTokens();
    if (!t) return null;
    if (!needsRefresh(t, leewaySec)) return t.access;
    try {
      return await refreshWithPolicy(t.refresh);
    } catch (err) {
      if (err instanceof RefreshFailure && err.fatal) {
        console.warn("[cockpit-jwt] refresh rejected, signing out:", err.message);
        clearTokens();
        return null;
      }
      // Transient — keep the tokens so the next attempt can still succeed.
      console.warn("[cockpit-jwt] refresh failed transiently, session kept:", err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  };

  // Older browsers / unusual contexts (e.g. some SW contexts) miss navigator.locks. Refresh
  // anyway — reuse-detection on the server still bounds blast radius if two tabs race.
  const locks = (typeof navigator !== "undefined" ? navigator.locks : undefined);
  if (!locks?.request) return run();
  return locks.request(REFRESH_LOCK, run);
}

/* ── Wake-up refresh ────────────────────────────────────────────────────────
 * Mobile is the problem case. Access tokens live 5 minutes, so a phone whose screen was off
 * for longer always needs a refresh — and lazily that happened inside the first request after
 * wake, when the radio is often still reconnecting, which used to end the session. Refreshing
 * on the wake events instead puts the POST where it can succeed: tab foregrounded, wider
 * leeway, and a failure costs nothing because the tokens survive it. */
let wakeRefreshInFlight = false;

async function refreshOnWake(): Promise<void> {
  if (wakeRefreshInFlight) return;
  const t = getTokens();
  if (!t) return;                                        // legacy pipe-Auth session, or signed out
  if (!needsRefresh(t, WAKE_REFRESH_LEEWAY_SEC)) return; // plenty of life left
  wakeRefreshInFlight = true;
  try {
    const access = await getAccessToken(WAKE_REFRESH_LEEWAY_SEC);
    if (!access) {
      // Fatal — getAccessToken already cleared the tokens. Bounce to the sign-in page with the
      // right banner now, rather than letting the user's next click fail into the legacy path.
      notifySessionExpired("refresh-failed");
    }
  } catch {
    // Transient: tokens intact, nothing worth telling the user. Next wake / request retries.
  } finally {
    wakeRefreshInFlight = false;
  }
}

if (typeof window !== "undefined") {
  try {
    // Tab foregrounded again — screen back on, or the browser switched back to this tab.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void refreshOnWake();
    });
    // Page Lifecycle: document unfrozen after the browser froze a backgrounded tab. Chromium
    // only; harmless where unsupported.
    document.addEventListener("resume", () => { void refreshOnWake(); });
    // Restored from the back/forward cache — iOS Safari's usual path back from long backgrounding.
    window.addEventListener("pageshow", (ev) => {
      if ((ev as PageTransitionEvent).persisted) void refreshOnWake();
    });
    // Radio came back: the first moment a refresh can actually succeed.
    window.addEventListener("online", () => { void refreshOnWake(); });
  } catch { /* no DOM (tests) — nothing to install */ }
}

const ACTIVE_EP_KEY_PREFIX = "activeEndpoint_";

/**
 * Reads the user's currently picked endpoint ID from localStorage. JWT mode rides this on
 * the `X-Active-Endpoint` header instead of baking it into the token (endpoint switching
 * stays a local-only operation, no /oauth/refresh round-trip).
 */
export function getActiveEndpointId(): number {
  const tokens = getTokens();
  if (!tokens) return 0;
  try {
    const raw = localStorage.getItem(`${ACTIVE_EP_KEY_PREFIX}${tokens.userId}`);
    if (!raw) return 0;
    const ep = JSON.parse(raw) as { ID?: number };
    return Number(ep.ID ?? 0);
  } catch { return 0; }
}
